if (!customElements.get('product-form')) {
  /**
   * Custom element that manages product form submissions and cart AJAX updates.
   * @extends HTMLElement
   */
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        this.form && this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      /**
       * Handles form submission via AJAX and updates cart/notification components.
       * @param {SubmitEvent} evt
       */
      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const choices = this.closest('#PopupModal-quickview.md-quick-options');
            if (choices) {
              choices.classList.remove('active', 'animate');
              removeTrapFocus(choices.openedBy);
              this.cart.setActiveElement(choices.openedBy);
            }
            this.cart.renderContents(response);
          })
          .catch((e) => {
            console.error(e);
            if (this.closest('.md-cart-rec, .md-local-options-card, #PopupModal-quickview.md-quick-options')) this.handleErrorMessage(window.cartStrings.error);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading-overlay__spinner').classList.add('hidden');
          });
      }

      /**
       * Displays or hides the form error message.
       * @param {string|boolean} [errorMessage=false]
       */
      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}

/**
 * Custom element for displaying a sticky cart modal on scroll.
 * @extends HTMLElement
 */
class stickyCartModal extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('.sticky-cart-button');
    this.form = document.getElementById(`product-form-${this.dataset.section}`);
    this.mainButton = this.form?.querySelector('[name="add"]');
    if (!this.button || !this.mainButton) return;
    this.productForm = this.form.closest('product-form');
    this.onScroll = this.update.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll);
    this.observer = new MutationObserver(() => this.syncState());
    this.observer.observe(this.productForm || this.form, {
      subtree: true, childList: true, characterData: true, attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'hidden', 'class']
    });
    this.syncState();
    this.update();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    this.observer?.disconnect();
    document.body.classList.remove('modal__sticky-cart');
  }

  syncState() {
    const busy = this.mainButton.classList.contains('loading');
    const disabled = this.mainButton.disabled || this.mainButton.getAttribute('aria-disabled') === 'true';
    this.button.disabled = disabled;
    this.button.setAttribute('aria-disabled', String(disabled));
    this.button.setAttribute('aria-busy', String(busy));
    this.button.classList.toggle('loading', busy);
    this.button.querySelector('.loading-overlay__spinner')?.classList.toggle('hidden', !busy);
    const source = this.mainButton.querySelector('.btn__texts');
    const target = this.button.querySelector('.btn__texts');
    if (source && target && source.innerHTML !== target.innerHTML) target.innerHTML = source.innerHTML;
    const wrapper = this.productForm?.querySelector('.product-form__error-message-wrapper');
    const message = wrapper && !wrapper.hidden ? wrapper.textContent.trim() : '';
    const status = this.querySelector('.md-sticky-error');
    if (status) {
      status.textContent = message;
      status.hidden = !message;
    }
  }

  update() {
    if (!this.mainButton) return;
    const visible = this.mainButton.getBoundingClientRect().bottom <= 0;
    this.classList.toggle('show-modal', visible);
    document.body.classList.toggle('modal__sticky-cart', visible);
    document.querySelector('.footer__content')?.classList.toggle('sticky-cart-modal_bottom_padding', visible);
  }

}
customElements.define('sticky-cart-modal', stickyCartModal);
